// src/components/SessionNotesModal.js
import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  VStack,
  HStack,
  Text,
  useToast,
  Checkbox,
  CheckboxGroup,
  Grid,
  GridItem,
  Box,
  SimpleGrid,
  Alert,
  AlertIcon,
  IconButton,
  Stack,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import supabase from '../supabaseClient';
import jsPDF from 'jspdf';
import { getLocalDateString } from '../utils/dateUtils';

// If you want to use a logo image, add this import:
// import logoImage from '../assets/logo.png'; // or wherever your logo is stored

const SessionNotesModal = ({ isOpen, onClose, session, therapistId, onSave }) => {
  // Initialize with empty form data
  const getInitialFormData = () => ({
    session_date: getLocalDateString(new Date()),
    present_with: [],
    present_with_other: '',
    child_behavior: [],
    child_behavior_other: '',
    activities: {
      activity_1: { name: '', supports: [], performance_notes: '' }
    },
    overall_progress: '',
    key_notes: '',
    plan_action: '',
    home_instruction_program: '',
    recommendations: ''
  });

  const [formData, setFormData] = useState(getInitialFormData());
  const [activityCount, setActivityCount] = useState(1);
  const [existingNote, setExistingNote] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [therapistInfo, setTherapistInfo] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [lastSessionAttendanceId, setLastSessionAttendanceId] = useState(null);
  const toast = useToast();
  
  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  // Single useEffect to handle modal open/close and session changes
  useEffect(() => {
    if (!isOpen) {
      // Don't reset anything when modal closes - preserve user input
      return;
    }
    
    if (isOpen && session) {
      // Create a unique identifier for this session instance
      const sessionInstanceId = session.attendance?.id || 
        `${session.id}-${session.attendance?.attendance_date || session.deckDate || getLocalDateString(new Date())}-${session.time}`;
      
      // Only reset form if this is a different session instance
      if (lastSessionAttendanceId !== sessionInstanceId) {
        setFormData(getInitialFormData());
        setActivityCount(1);
        setExistingNote(null);
        setLastSessionAttendanceId(sessionInstanceId);
        
        // Fetch data for new session
        fetchTherapistInfo();
        fetchExistingNote();
      }
    }
  }, [isOpen, session?.id, session?.attendance?.id, session?.time]);
  
  // Safety checks - move these BEFORE the useEffect
  if (!session) return null;
  
  /*
  if (!session.attendance) {
    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader bg="#24b5e8" color="white">
            Session Notes
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody p={6}>
            <VStack spacing={4}>
              <Text>Session attendance record is required to create notes.</Text>
              <Text fontSize="sm" color="gray.600">
                Please mark attendance for this session first before adding notes.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }
  */
  
  const fetchTherapistInfo = async () => {
    try {
      const { data, error } = await supabase
        .from('therapists')
        .select('name, specialization, signature_image')
        .eq('id', therapistId)
        .single();
      
      if (error) throw error;
      setTherapistInfo(data);
    } catch (error) {
      console.error('Error fetching therapist info:', error);
    }
  };

  const fetchExistingNote = async () => {
    try {
      if (!session) {
        setExistingNote(null);
        return;
      }
  
      const sessionDate =
        session.attendance?.attendance_date ||
        session.deckDate ||
        getLocalDateString(new Date());
      
      // Try to find note by attendance ID first (if exists), otherwise by original session details
      let { data, error } = { data: null, error: null };
      
      if (session.attendance?.id) {
        // Look for note linked to attendance record
        ({ data, error } = await supabase
          .from('session_notes')
          .select('*')
          .eq('session_id', session.attendance.id)
          .maybeSingle());
      }
      
      // If no note found with attendance ID (or no attendance), look by original session details
      if (!data && !error) {
        ({ data, error } = await supabase
          .from('session_notes')
          .select('*')
          .eq('original_session_id', session.id)
          .eq('session_date', sessionDate)
          .eq('session_time', session.time)
          .maybeSingle());
      }
      
      if (error && error.code !== 'PGNF') throw error;
      
      if (data) {
        setExistingNote(data);
        // Calculate activity count from existing data
        const existingActivities = data.activities || {};
        const count = Object.keys(existingActivities).length;
        setActivityCount(Math.max(1, count));
        
        // Simple normalization without aggressive text processing
        const normalizedActivities = {};
        Object.keys(existingActivities).forEach(key => {
          const activity = existingActivities[key];
          normalizedActivities[key] = {
            name: activity.name || '',
            supports: activity.supports || [],
            performance_notes: activity.performance_notes || ''
          };
        });
  
        // Populate form with existing data
        setFormData({
          session_date: data.session_date || sessionDate,
          present_with: data.present_with || [],
          present_with_other: data.present_with_other || '',
          child_behavior: data.child_behavior || [],
          child_behavior_other: data.child_behavior_other || '',
          activities: normalizedActivities,
          overall_progress: data.overall_progress || '',
          key_notes: data.key_notes || '',
          plan_action: data.plan_action || '',
          home_instruction_program: data.home_instruction_program || '',
          recommendations: data.recommendations || ''
        });
      } else {
        setExistingNote(null);
        // Set session date for new notes
        setFormData(prev => ({
          ...prev,
          session_date: sessionDate
        }));
      }
    } catch (error) {
      console.error('Error fetching existing note:', error);
    }
  };

  // Helper function to load image and convert to base64
  const loadImageAsBase64 = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const base64 = canvas.toDataURL('image/png');
        resolve(base64);
      };
      img.onerror = reject;
      img.src = src;
    });
  };

  const generatePDF = async () => {
    setIsExporting(true);
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 12;
      let yPosition = margin;
      
      // Tiny Triumphs brand colors (matching your modal)
      const colors = {
        primary: [36, 181, 232],      // #24b5e8 - Your primary blue
        yellow: [255, 172, 17],       // #ffac11 - Your yellow
        green: [171, 219, 21],        // #abdb15 - Your green  
        purple: [220, 66, 145],       // #dc4291 - Your purple
        lightYellow: [255, 248, 220], // Light yellow background
        lightGreen: [240, 251, 207],  // Light green background
        lightBlue: [224, 247, 255],   // Light blue background
        lightPurple: [252, 240, 249], // Light purple background
        white: [255, 255, 255],
        gray: [128, 128, 128],
        black: [0, 0, 0],
        darkGray: [64, 64, 64],
        lightGray: [248, 249, 250]
      };
      
      // Helper function to check if we need a new page
      const checkPageBreak = (requiredHeight) => {
        if (yPosition + requiredHeight > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };
      
      // Helper functions
      const addText = (text, x, y, options = {}) => {
        const { fontSize = 9, fontWeight = 'normal', color = colors.darkGray, maxWidth = null, lineHeight = 1.3 } = options;
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', fontWeight);
        doc.setTextColor(...color);
        
        if (maxWidth) {
          const lines = doc.splitTextToSize(text, maxWidth);
          lines.forEach((line, index) => {
            doc.text(line, x, y + (index * fontSize * lineHeight));
          });
          return y + (lines.length * fontSize * lineHeight);
        } else {
          doc.text(text, x, y);
          return y + (fontSize * lineHeight);
        }
      };
      
      const createColoredSection = (title, x, y, width, height, headerColor, bgColor) => {
        // Background
        doc.setFillColor(...bgColor);
        doc.roundedRect(x, y, width, height, 3, 3, 'F');
        
        // Header bar (matching your modal style)
        doc.setFillColor(...headerColor);
        doc.roundedRect(x + 2, y + 2, width - 4, 8, 1, 1, 'F');
        
        // Header text
        addText(title, x + 4, y + 7, { fontSize: 9, fontWeight: 'bold', color: [0, 0, 0] });
        
        return y + 14; // Return content start position
      };
      
      // HEADER - Logo on left, text on right
      
      // LEFT SIDE - Logo image
      try {
        const logoBase64 = await loadImageAsBase64('/tt-logo.png');
        doc.addImage(logoBase64, 'PNG', 3, 0, 75, 25); // x, y, width, height
      } catch (error) {
        console.warn('Logo not found, using text fallback:', error);
        // Fallback to text logo if image fails to load
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...colors.white);
        doc.text('TT', 8, 14);
      }
      
      // RIGHT SIDE - Company name and specialization
      const specializationInfo = getSpecializationInfo();
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...colors.black);
      doc.text('3rd Floor Allegro Center, Pasong Tamo Ext., Makati', pageWidth - 12, 17, { align: 'right' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...colors.gray);
      doc.text('+639171600036 | tinytriumphsph@gmail.com', pageWidth - 12, 21, { align: 'right' });

      doc.setFillColor(...colors.primary);
      doc.rect(12, 23, pageWidth - 23, 2, 'F');
      
      yPosition = 28;
      
      // CLIENT INFO - styled with purple labels and white value backgrounds
      let clientInfo = `${session.client.name}`;
      if (session.client.nickname) clientInfo += ` "${session.client.nickname}"`;
      const dateInfo = new Date(formData.session_date).toLocaleDateString();
      
      const clientInfoY = yPosition;
      
      // Client Name section
      // Purple background for "Client Name:" label
      doc.setFillColor(...colors.purple);
      doc.roundedRect(margin + 2, clientInfoY - 1, 25, 7, 0, 0, 'F');
      addText('Client Name:', margin + 4, clientInfoY + 3.5, { fontSize: 10, fontWeight: 'bold', color: colors.white });
      
      // White background rectangle for client name value
      const clientNameWidth = Math.max(60, doc.getTextWidth(clientInfo, { fontSize: 12 }) + 8);
      doc.setFillColor(...colors.white);
      doc.roundedRect(margin + 27, clientInfoY - 0.8, clientNameWidth, 6.7, 0, 0, 'F');
      doc.setDrawColor(...colors.gray);
      doc.setLineWidth(0.3);
      doc.roundedRect(margin + 27, clientInfoY - 0.8, clientNameWidth, 6.7, 0, 0, 'S');
      addText(clientInfo, margin + 29, clientInfoY + 3.5, { fontSize: 11, fontWeight: 'normal', color: colors.darkGray });
      
      // Date section (right side)
      const dateStartX = pageWidth - 55;
      
      // Purple background for "Date:" label
      doc.setFillColor(...colors.purple);
      doc.roundedRect(dateStartX, clientInfoY - 1, 25, 7, 0, 0, 'F');
      addText('Date:', dateStartX + 2, clientInfoY + 3.5, { fontSize: 10, fontWeight: 'bold', color: colors.white });
      
      // White background rectangle for date value
      const dateWidth = Math.max(25, doc.getTextWidth(dateInfo, { fontSize: 12 }) + 8);
      doc.setFillColor(...colors.white);
      doc.roundedRect(dateStartX + 17, clientInfoY - 0.8, dateWidth, 6.7, 0, 0, 'F');
      doc.setDrawColor(...colors.gray);
      doc.setLineWidth(0.3);
      doc.roundedRect(dateStartX + 17, clientInfoY - 0.8, dateWidth, 6.7, 0, 0, 'S');
      addText(dateInfo, dateStartX + 19, clientInfoY + 3.5, { fontSize: 11, fontWeight: 'normal', color: colors.darkGray });
      
      yPosition += 7;
      
      // SUBJECTIVE SECTION - Make height dynamic based on content
      // Calculate required height for present with content
      let presentText = '';
      if (formData.present_with.length > 0) {
        presentText += formData.present_with.join(', ');
        if (formData.present_with.includes('Other') && formData.present_with_other) {
          presentText += ` (${formData.present_with_other})`;
        }
      } else {
        presentText += 'Not specified';
      }
      
      // Calculate required height for behavior content
      let behaviorText = '';
      if (formData.child_behavior.length > 0) {
        behaviorText += formData.child_behavior.join(', ');
        if (formData.child_behavior_other) {
          behaviorText += `, ${formData.child_behavior_other}`;
        }
      } else {
        behaviorText += 'Not specified';
      }
      
      // Calculate text heights - reduced height for present with and added margin
      const presentTextLines = doc.splitTextToSize(presentText, pageWidth - (margin * 2) - 12);
      const behaviorTextLines = doc.splitTextToSize(behaviorText, pageWidth - (margin * 2) - 12);
      
      const presentCellHeight = Math.max(10, presentTextLines.length * 9 * 0.65 + 6);
      const behaviorCellHeight = Math.max(
        20,
        11 + behaviorTextLines.length * 10 * 0.48 + 8
      );

      const subjectiveSectionHeight = presentCellHeight + behaviorCellHeight + 12;
      
      // Check if we need a page break for subjective section
      checkPageBreak(subjectiveSectionHeight);
      
      let subjY = createColoredSection('SUBJECTIVE', margin, yPosition, pageWidth - (margin * 2), subjectiveSectionHeight, colors.yellow, colors.lightYellow);
      
      // Present with - with dynamic cell background (reduced size)
      doc.setFillColor(...colors.white);
      doc.roundedRect(margin + 4, subjY - 2, pageWidth - (margin * 2) - 8, presentCellHeight, 2, 2, 'F');
      doc.setDrawColor(...colors.gray);
      doc.setLineWidth(0.3);
      doc.roundedRect(margin + 4, subjY - 2, pageWidth - (margin * 2) - 8, presentCellHeight, 2, 2, 'S');
      
      subjY = addText('Present with: ', margin + 6, subjY + 4, { fontSize: 10, maxWidth: pageWidth - (margin * 2) - 12, lineHeight: 0.6, fontWeight: 'bold' });
      addText(presentText, margin + 30, subjY - 6, { fontSize: 10, maxWidth: pageWidth - (margin * 2) - 12, lineHeight: 0.6 });

      // Update position for behavior section with added margin
      subjY += presentCellHeight - 8; // Added 4 units margin

      // Child behavior - with dynamic cell background
      doc.setFillColor(...colors.white);
      doc.roundedRect(margin + 4, subjY - 2, pageWidth - (margin * 2) - 8, behaviorCellHeight, 2, 2, 'F');
      doc.setDrawColor(...colors.gray);
      doc.setLineWidth(0.3);
      doc.roundedRect(margin + 4, subjY - 2, pageWidth - (margin * 2) - 8, behaviorCellHeight, 2, 2, 'S');
      
      addText('During the session, child was noted of the following: ', margin + 6, subjY + 2, { fontSize: 10, maxWidth: pageWidth - (margin * 2) - 12, lineHeight: 0.6, fontWeight: 'bold' });
      addText(behaviorText, margin + 8, subjY + 7, { fontSize: 10, maxWidth: pageWidth - (margin * 2) - 14, lineHeight: 0.4 });

      // Update yPosition to account for dynamic subjective section
      yPosition += subjectiveSectionHeight + 5;
      
      // ACTIVITIES SECTION - Make cell heights dynamic
      const activities = Object.entries(formData.activities).slice(0, 6);
      const numberOfRows = Math.ceil(activities.length / 2);
      
      // Calculate dynamic cell heights for each activity
      const cellWidth = (pageWidth - (margin * 2) - 12) / 2;
      let maxRowHeights = [];
      
      // Calculate height needed for each row
      for (let row = 0; row < numberOfRows; row++) {
        let maxHeightInRow = 41; // Minimum height
        
        for (let col = 0; col < 2; col++) {
          const activityIndex = row * 2 + col;
          if (activityIndex < activities.length) {
            const [key, activity] = activities[activityIndex];
            
            // Calculate required height for this activity
            let requiredHeight = 10; // Base height for activity name and supports
            
            // Calculate height for activity name
            if (activity.name) {
              const nameLines = doc.splitTextToSize(activity.name, cellWidth - 20); // Reduced width for activity name
              requiredHeight += nameLines.length * 1.0;
            }
            
            if (activity.performance_notes) {
              const performanceLines = doc.splitTextToSize(activity.performance_notes, cellWidth - 6);
              requiredHeight += performanceLines.length * 8 * 0.4 + 8; // Performance notes height
            }
            
            maxHeightInRow = Math.max(maxHeightInRow, requiredHeight);
          }
        }
        maxRowHeights[row] = maxHeightInRow;
      }
      
      const pageBottomAct = pageHeight - margin;
      const activityChunkOverheadLast = 22;
      const activityChunkOverheadMid = 18;

      let startRow = 0;
      while (startRow < numberOfRows) {
        let endRow = startRow;
        let chunkGrid = 0;

        while (endRow < numberOfRows) {
          const candidateChunk = chunkGrid + maxRowHeights[endRow] + 2;
          const wouldCompleteSection = endRow + 1 >= numberOfRows;
          const overhead = wouldCompleteSection ? activityChunkOverheadLast : activityChunkOverheadMid;
          if (yPosition + candidateChunk + overhead > pageBottomAct) {
            break;
          }
          chunkGrid = candidateChunk;
          endRow++;
        }

        if (endRow === startRow) {
          if (yPosition > margin + 1) {
            doc.addPage();
            yPosition = margin;
            continue;
          }
          chunkGrid = maxRowHeights[startRow] + 2;
          endRow = startRow + 1;
        }

        const isLastChunk = endRow >= numberOfRows;
        const activitySectionHeight =
          chunkGrid + (isLastChunk ? activityChunkOverheadLast : activityChunkOverheadMid);

        const activitySectionTitle =
          startRow === 0 ? 'ACTIVITIES & PERFORMANCE' : 'ACTIVITIES & PERFORMANCE (cont.)';

        const actY = createColoredSection(
          activitySectionTitle,
          margin,
          yPosition,
          pageWidth - (margin * 2),
          activitySectionHeight,
          colors.green,
          colors.lightGreen
        );

        let currentRowY = actY - 2;

        for (let index = startRow * 2; index < Math.min(endRow * 2, activities.length); index++) {
          const row = Math.floor(index / 2);
          const col = index % 2;
          const cellX = margin + 4 + col * (cellWidth + 4);
          const cellHeight = maxRowHeights[row];
          const [, activity] = activities[index];

          if (col === 0 && row > startRow) {
            currentRowY += maxRowHeights[row - 1] + 2;
          }

          const cellY = currentRowY;

          doc.setFillColor(...colors.white);
          doc.roundedRect(cellX, cellY, cellWidth, cellHeight, 2, 2, 'F');
          doc.setDrawColor(...colors.gray);
          doc.setLineWidth(0.3);
          doc.roundedRect(cellX, cellY, cellWidth, cellHeight, 2, 2, 'S');

          let cellContentY = cellY + 3;

          addText(`Activity ${index + 1}:`, cellX + 2, cellContentY + 1, { fontSize: 9, fontWeight: 'bold' });
          if (activity.name) {
            cellContentY = addText(activity.name, cellX + 18, cellContentY + 1, {
              fontSize: 9,
              maxWidth: cellWidth - 20,
              lineHeight: 0.4,
            });
          }

          if (activity.supports.length > 0) {
            addText('Supports Given: ', cellX + 2, cellContentY + 2, {
              fontSize: 8,
              maxWidth: cellWidth - 4,
              lineHeight: 1.0,
              fontWeight: 'bold',
            });
            addText(activity.supports.join(', '), cellX + 25, cellContentY + 2, {
              fontSize: 8,
              maxWidth: cellWidth - 4,
              lineHeight: 1.0,
            });
          }

          if (activity.performance_notes) {
            addText('Performance: ', cellX + 2, cellContentY + 6, {
              fontSize: 8,
              maxWidth: cellWidth - 4,
              lineHeight: 0.2,
              fontWeight: 'bold',
            });
            addText(activity.performance_notes, cellX + 2, cellContentY + 10, {
              fontSize: 8,
              maxWidth: cellWidth - 3,
              lineHeight: 0.4,
            });
          }
        }

        if (isLastChunk) {
          const legendY = actY + chunkGrid - 2;
          doc.setFillColor(...colors.lightGray);
          doc.rect(margin + 4, legendY, pageWidth - (margin * 2) - 8, 6, 'F');
          addText(
            'Legend: IND (independent), CGA (contact guard assistance), MIN (minimal), MOD (moderate), MAX (maximum), VER (verbal), PHY (physical), GES (gestural)',
            margin + 6,
            legendY + 4,
            { fontSize: 6, fontWeight: 'bold', color: colors.gray }
          );
        }

        yPosition += activitySectionHeight + 1;

        startRow = endRow;
        if (startRow < numberOfRows) {
          doc.addPage();
          yPosition = margin;
        }
      }
      
      // SIDE-BY-SIDE: OVERALL PROGRESS AND PLAN/RECOMMENDATIONS SECTIONS
      const sectionWidth = (pageWidth - (margin * 2)) / 2; // Split into two columns with gap
      const leftSectionX = margin;
      const rightSectionX = margin + sectionWidth + 0.5;
      const sectionWrapW = sectionWidth - 12;
      const progressValueWrapW = sectionWidth - 26;
      const planValueWrapW = sectionWidth - 22;
      const recommendationsTrimmed =
        formData.recommendations && String(formData.recommendations).trim()
          ? String(formData.recommendations).trim()
          : '';
      const fullRecWrapW = pageWidth - margin * 2 - 16;
      const bodyFs = 9;
      const lhCol = 0.68;
      const titledPadTop = 6;
      const titledGapTitleBody = 4;
      const titledPadBottom = 16;
      /** Extra space below last baseline (descenders, rounding, smart quotes). */
      const titledBottomSlack = bodyFs * 1.2;

      const splitWrap = (w) => Math.max(8, w - 6);

      /** Wrap each newline-separated paragraph separately (splitTextToSize can mishandle long multi-line blobs). */
      const buildWrappedBodyLines = (body, innerMaxWidth) => {
        doc.setFontSize(bodyFs);
        doc.setFont('helvetica', 'normal');
        const normalized = String(body ?? '').replace(/\r\n/g, '\n');
        const linesOut = [];
        normalized.split('\n').forEach((segment) => {
          const s = segment.replace(/\u00a0/g, ' ').trim();
          if (!s) return;
          doc.splitTextToSize(s, innerMaxWidth).forEach((wl) => linesOut.push(wl));
        });
        return linesOut;
      };

      const computeTitledBoxLayout = (title, body, wrapW) => {
        const w = splitWrap(wrapW);
        doc.setFontSize(bodyFs);
        doc.setFont('helvetica', 'bold');
        const titleLines = doc.splitTextToSize(String(title), w);
        doc.setFont('helvetica', 'normal');
        let bodyLines = buildWrappedBodyLines(body, w);
        if (!bodyLines.length && String(body ?? '').trim()) {
          bodyLines = doc.splitTextToSize(String(body).trim(), w);
        }
        const titleTopOffset = titledPadTop + bodyFs * 1.05;
        const titleDepth = titleLines.length * bodyFs * lhCol;
        const bodyDepth = bodyLines.length * bodyFs * lhCol;
        const h =
          titleTopOffset +
          titleDepth +
          titledGapTitleBody +
          bodyDepth +
          titledPadBottom +
          titledBottomSlack;
        return { h, w, titleLines, bodyLines, titleTopOffset };
      };

      const getTitledBoxHeight = (title, body, wrapW) =>
        computeTitledBoxLayout(title, body, wrapW).h;

      const drawTitledWhiteBox = (x, top, boxWidth, title, body, wrapW) => {
        const { h, titleLines, bodyLines, titleTopOffset } = computeTitledBoxLayout(title, body, wrapW);
        doc.setFillColor(...colors.white);
        doc.roundedRect(x, top, boxWidth, h, 2, 2, 'F');
        doc.setDrawColor(...colors.gray);
        doc.setLineWidth(0.3);
        doc.roundedRect(x, top, boxWidth, h, 2, 2, 'S');

        doc.setTextColor(...colors.darkGray);
        doc.setFontSize(bodyFs);
        let lineY = top + titleTopOffset;
        doc.setFont('helvetica', 'bold');
        titleLines.forEach((line, i) => {
          doc.text(line, x + 2, lineY + i * bodyFs * lhCol);
        });
        lineY += titleLines.length * bodyFs * lhCol + titledGapTitleBody;
        doc.setFont('helvetica', 'normal');
        bodyLines.forEach((line, i) => {
          doc.text(line, x + 2, lineY + i * bodyFs * lhCol);
        });
        return h;
      };

      const progressBlockHeight = () => {
        if (!formData.overall_progress) return 0;
        doc.setFontSize(bodyFs);
        doc.setFont('helvetica', 'normal');
        const lines = doc.splitTextToSize(formData.overall_progress, progressValueWrapW);
        return Math.max(14, lines.length * bodyFs * 0.6 + 16);
      };

      const planBlockHeight = () => {
        if (!formData.plan_action) return 0;
        doc.setFontSize(bodyFs);
        doc.setFont('helvetica', 'normal');
        const lines = doc.splitTextToSize(formData.plan_action, planValueWrapW);
        return Math.max(14, lines.length * bodyFs * 0.6 + 16);
      };

      const progressH = progressBlockHeight();
      const planH = planBlockHeight();

      let leftSectionHeight = 14;
      if (formData.overall_progress) leftSectionHeight += progressH;
      if (formData.key_notes) {
        const keyH = getTitledBoxHeight('KEY NOTES:', formData.key_notes, sectionWrapW);
        const keyLead = formData.overall_progress ? -6 : 4;
        leftSectionHeight += keyLead + keyH;
      }
      leftSectionHeight = Math.max(50, leftSectionHeight);

      let planCursor = 14;
      let rightContentBottom = 14;
      if (formData.plan_action) {
        planCursor += planH + 1;
        rightContentBottom = planCursor;
      }
      if (formData.home_instruction_program) {
        const hipH = getTitledBoxHeight(
          'HIP (Home Instruction Program):',
          formData.home_instruction_program,
          sectionWrapW
        );
        const hipLead = formData.plan_action ? -6 : 4;
        const hipTopRel = planCursor + hipLead;
        rightContentBottom = Math.max(rightContentBottom, hipTopRel + hipH);
        planCursor += hipH - 8;
      }

      const rightSectionHeight = Math.max(50, rightContentBottom + 8);

      const finalSectionHeight = Math.max(leftSectionHeight, rightSectionHeight);

      checkPageBreak(finalSectionHeight + 12);

      let progressY = createColoredSection('OVERALL PROGRESS', leftSectionX, yPosition, sectionWidth, finalSectionHeight, colors.primary, colors.lightBlue);

      if (formData.overall_progress) {
        const progressText = formData.overall_progress;
        const progressCellHeight = progressH;

        doc.setFillColor(...colors.white);
        doc.roundedRect(leftSectionX + 4, progressY - 2, sectionWidth - 8, progressCellHeight - 6, 2, 2, 'F');
        doc.setDrawColor(...colors.gray);
        doc.setLineWidth(0.3);
        doc.roundedRect(leftSectionX + 4, progressY - 2, sectionWidth - 8, progressCellHeight - 6, 2, 2, 'S');

        addText('Progress: ', leftSectionX + 6, progressY + 2, { fontSize: 9, fontWeight: 'bold' });
        addText(progressText, leftSectionX + 22, progressY + 2, { fontSize: bodyFs, maxWidth: progressValueWrapW, lineHeight: 0.6 });
        progressY += progressCellHeight;
      }

      if (formData.key_notes) {
        const keyTop = formData.overall_progress ? progressY - 6 : progressY + 4;
        drawTitledWhiteBox(
          leftSectionX + 4,
          keyTop,
          sectionWidth - 8,
          'KEY NOTES:',
          formData.key_notes,
          sectionWrapW
        );
      }

      let planY = createColoredSection('PLAN / RECOMMENDATIONS', rightSectionX, yPosition, sectionWidth, finalSectionHeight, colors.purple, colors.lightPurple);

      if (formData.plan_action) {
        const planText = formData.plan_action;
        const planCellHeight = planH;

        doc.setFillColor(...colors.white);
        doc.roundedRect(rightSectionX + 4, planY - 2, sectionWidth - 8, planCellHeight - 6, 2, 2, 'F');
        doc.setDrawColor(...colors.gray);
        doc.setLineWidth(0.3);
        doc.roundedRect(rightSectionX + 4, planY - 2, sectionWidth - 8, planCellHeight - 6, 2, 2, 'S');

        addText('Plan: ', rightSectionX + 6, planY + 2, { fontSize: 9, fontWeight: 'bold' });
        addText(planText, rightSectionX + 17, planY + 2, { fontSize: bodyFs, maxWidth: planValueWrapW, lineHeight: 0.6 });
        planY += planCellHeight + 1;
      }

      if (formData.home_instruction_program) {
        const hipTop = formData.plan_action ? planY - 6 : planY + 4;
        const hipDrawnH = drawTitledWhiteBox(
          rightSectionX + 4,
          hipTop,
          sectionWidth - 8,
          'HIP (Home Instruction Program):',
          formData.home_instruction_program,
          sectionWrapW
        );
        planY += hipDrawnH - 8;
      }

      // Update yPosition to account for dynamic sections
      yPosition += finalSectionHeight + 1;

      if (recommendationsTrimmed) {
        checkPageBreak(
          getTitledBoxHeight('Additional Recommendations:', recommendationsTrimmed, fullRecWrapW) + 40
        );
        const recTop = yPosition + 4;
        const recDrawnH = drawTitledWhiteBox(
          margin + 4,
          recTop,
          pageWidth - margin * 2 - 8,
          'Additional Recommendations:',
          recommendationsTrimmed,
          fullRecWrapW
        );
        yPosition += recDrawnH + 10;
      }

      // Check if we need a page break for signature
      checkPageBreak(30);
      
      // THERAPIST SIGNATURE (matching your modal's therapist info section)
      doc.setFillColor(...colors.lightGray);
      doc.roundedRect(margin, yPosition, pageWidth - (margin * 2), 25, 3, 3, 'F');
      
      yPosition += 4;
      addText('Prepared by:', margin + 4, yPosition, { fontSize: 8, fontWeight: 'bold' });
      yPosition += 10;
      if (therapistInfo) {
        addText(`${therapistInfo.name}, ${getSpecializationInfo().designation}`, margin + 4, yPosition, { fontSize: 9, fontWeight: 'bold' });
        yPosition += 4;
        addText('Therapist In-Charge', margin + 4, yPosition, { fontSize: 8, color: colors.gray });
        //addText(getSpecializationInfo().full, margin + 4, yPosition, { fontSize: 8, color: colors.gray });
      }
      
      // Contact info
      yPosition += 3;
      addText('Tiny Triumphs Therapy Center', margin + 4, yPosition, { fontSize: 7, color: colors.gray });
      
      // Signature - use saved signature image if available, otherwise show signature line
      if (therapistInfo?.signature_image) {
        try {
          // Add the signature image
          doc.addImage(therapistInfo.signature_image, 'PNG', margin + 4, yPosition - 16, 40, 8);
        } catch (error) {
          console.warn('Error adding signature image:', error);
          // Fallback to signature line if image fails
          doc.setDrawColor(...colors.gray);
          doc.line(pageWidth - margin - 50, yPosition - 4, pageWidth - margin - 10, yPosition - 4);
          addText('Signature', pageWidth - margin - 45, yPosition - 1, { fontSize: 7, color: colors.gray });
        }
      } else {
        // No signature image available, show signature line
        doc.setDrawColor(...colors.gray);
        doc.line(pageWidth - margin - 50, yPosition - 4, pageWidth - margin - 10, yPosition - 4);
        addText('Signature', pageWidth - margin - 45, yPosition - 1, { fontSize: 7, color: colors.gray });
      }
      
      // Generate filename
      const clientName = session.client.name.replace(/\s+/g, '_');
      const sessionDate = new Date(formData.session_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\//g, '-');
      const filename = `${getSpecializationInfo().abbrev}_Session_Note_${clientName}_${sessionDate}.pdf`;
      
      doc.save(filename);
      
      toast({
        title: 'PDF exported successfully',
        description: `${filename} has been downloaded`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: 'Error generating PDF',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Add this function after the existing generatePDF function
  const generateAndUploadPDF = async () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 12;
      let yPosition = margin;
      
      // Tiny Triumphs brand colors (matching your modal)
      const colors = {
        primary: [36, 181, 232],      // #24b5e8 - Your primary blue
        yellow: [255, 172, 17],       // #ffac11 - Your yellow
        green: [171, 219, 21],        // #abdb15 - Your green  
        purple: [220, 66, 145],       // #dc4291 - Your purple
        lightYellow: [255, 248, 220], // Light yellow background
        lightGreen: [240, 251, 207],  // Light green background
        lightBlue: [224, 247, 255],   // Light blue background
        lightPurple: [252, 240, 249], // Light purple background
        white: [255, 255, 255],
        gray: [128, 128, 128],
        black: [0, 0, 0],
        darkGray: [64, 64, 64],
        lightGray: [248, 249, 250]
      };
      
      // Helper function to check if we need a new page
      const checkPageBreak = (requiredHeight) => {
        if (yPosition + requiredHeight > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };
      
      // Helper functions
      const addText = (text, x, y, options = {}) => {
        const { fontSize = 9, fontWeight = 'normal', color = colors.darkGray, maxWidth = null, lineHeight = 1.3 } = options;
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', fontWeight);
        doc.setTextColor(...color);
        
        if (maxWidth) {
          const lines = doc.splitTextToSize(text, maxWidth);
          lines.forEach((line, index) => {
            doc.text(line, x, y + (index * fontSize * lineHeight));
          });
          return y + (lines.length * fontSize * lineHeight);
        } else {
          doc.text(text, x, y);
          return y + (fontSize * lineHeight);
        }
      };
      
      const createColoredSection = (title, x, y, width, height, headerColor, bgColor) => {
        // Background
        doc.setFillColor(...bgColor);
        doc.roundedRect(x, y, width, height, 3, 3, 'F');
        
        // Header bar (matching your modal style)
        doc.setFillColor(...headerColor);
        doc.roundedRect(x + 2, y + 2, width - 4, 8, 1, 1, 'F');
        
        // Header text
        addText(title, x + 4, y + 7, { fontSize: 9, fontWeight: 'bold', color: [0, 0, 0] });
        
        return y + 14; // Return content start position
      };
      
      // HEADER - Logo on left, text on right
      
      // LEFT SIDE - Logo image
      try {
        const logoBase64 = await loadImageAsBase64('/tt-logo.png');
        doc.addImage(logoBase64, 'PNG', 3, 0, 75, 25); // x, y, width, height
      } catch (error) {
        console.warn('Logo not found, using text fallback:', error);
        // Fallback to text logo if image fails to load
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...colors.white);
        doc.text('TT', 8, 14);
      }
      
      // RIGHT SIDE - Company name and specialization
      const specializationInfo = getSpecializationInfo();
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...colors.black);
      doc.text('3rd Floor Allegro Center, Pasong Tamo Ext., Makati', pageWidth - 12, 17, { align: 'right' });
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...colors.gray);
      doc.text('+639171600036 | tinytriumphsph@gmail.com', pageWidth - 12, 21, { align: 'right' });

      doc.setFillColor(...colors.primary);
      doc.rect(12, 23, pageWidth - 23, 2, 'F');
      
      yPosition = 28;
      
      // CLIENT INFO - styled with purple labels and white value backgrounds
      let clientInfo = `${session.client.name}`;
      if (session.client.nickname) clientInfo += ` "${session.client.nickname}"`;
      const dateInfo = new Date(formData.session_date).toLocaleDateString();
      
      const clientInfoY = yPosition;
      
      // Client Name section
      // Purple background for "Client Name:" label
      doc.setFillColor(...colors.purple);
      doc.roundedRect(margin + 2, clientInfoY - 1, 25, 7, 0, 0, 'F');
      addText('Client Name:', margin + 4, clientInfoY + 3.5, { fontSize: 10, fontWeight: 'bold', color: colors.white });
      
      // White background rectangle for client name value
      const clientNameWidth = Math.max(60, doc.getTextWidth(clientInfo, { fontSize: 12 }) + 8);
      doc.setFillColor(...colors.white);
      doc.roundedRect(margin + 27, clientInfoY - 0.8, clientNameWidth, 6.7, 0, 0, 'F');
      doc.setDrawColor(...colors.gray);
      doc.setLineWidth(0.3);
      doc.roundedRect(margin + 27, clientInfoY - 0.8, clientNameWidth, 6.7, 0, 0, 'S');
      addText(clientInfo, margin + 29, clientInfoY + 3.5, { fontSize: 11, fontWeight: 'normal', color: colors.darkGray });
      
      // Date section (right side)
      const dateStartX = pageWidth - 55;
      
      // Purple background for "Date:" label
      doc.setFillColor(...colors.purple);
      doc.roundedRect(dateStartX, clientInfoY - 1, 25, 7, 0, 0, 'F');
      addText('Date:', dateStartX + 2, clientInfoY + 3.5, { fontSize: 10, fontWeight: 'bold', color: colors.white });
      
      // White background rectangle for date value
      const dateWidth = Math.max(25, doc.getTextWidth(dateInfo, { fontSize: 12 }) + 8);
      doc.setFillColor(...colors.white);
      doc.roundedRect(dateStartX + 17, clientInfoY - 0.8, dateWidth, 6.7, 0, 0, 'F');
      doc.setDrawColor(...colors.gray);
      doc.setLineWidth(0.3);
      doc.roundedRect(dateStartX + 17, clientInfoY - 0.8, dateWidth, 6.7, 0, 0, 'S');
      addText(dateInfo, dateStartX + 19, clientInfoY + 3.5, { fontSize: 11, fontWeight: 'normal', color: colors.darkGray });
      
      yPosition += 7;
      
      // SUBJECTIVE SECTION - Make height dynamic based on content
      // Calculate required height for present with content
      let presentText = '';
      if (formData.present_with.length > 0) {
        presentText += formData.present_with.join(', ');
        if (formData.present_with.includes('Other') && formData.present_with_other) {
          presentText += ` (${formData.present_with_other})`;
        }
      } else {
        presentText += 'Not specified';
      }
      
      // Calculate required height for behavior content
      let behaviorText = '';
      if (formData.child_behavior.length > 0) {
        behaviorText += formData.child_behavior.join(', ');
        if (formData.child_behavior_other) {
          behaviorText += `, ${formData.child_behavior_other}`;
        }
      } else {
        behaviorText += 'Not specified';
      }
      
      // Calculate text heights - reduced height for present with and added margin
      const presentTextLines = doc.splitTextToSize(presentText, pageWidth - (margin * 2) - 12);
      const behaviorTextLines = doc.splitTextToSize(behaviorText, pageWidth - (margin * 2) - 12);
      
      const presentCellHeight = Math.max(10, presentTextLines.length * 9 * 0.65 + 6);
      const behaviorCellHeight = Math.max(
        20,
        11 + behaviorTextLines.length * 10 * 0.48 + 8
      );

      const subjectiveSectionHeight = presentCellHeight + behaviorCellHeight + 12;
      
      // Check if we need a page break for subjective section
      checkPageBreak(subjectiveSectionHeight);
      
      let subjY = createColoredSection('SUBJECTIVE', margin, yPosition, pageWidth - (margin * 2), subjectiveSectionHeight, colors.yellow, colors.lightYellow);
      
      // Present with - with dynamic cell background (reduced size)
      doc.setFillColor(...colors.white);
      doc.roundedRect(margin + 4, subjY - 2, pageWidth - (margin * 2) - 8, presentCellHeight, 2, 2, 'F');
      doc.setDrawColor(...colors.gray);
      doc.setLineWidth(0.3);
      doc.roundedRect(margin + 4, subjY - 2, pageWidth - (margin * 2) - 8, presentCellHeight, 2, 2, 'S');
      
      subjY = addText('Present with: ', margin + 6, subjY + 4, { fontSize: 10, maxWidth: pageWidth - (margin * 2) - 12, lineHeight: 0.6, fontWeight: 'bold' });
      addText(presentText, margin + 30, subjY - 6, { fontSize: 10, maxWidth: pageWidth - (margin * 2) - 12, lineHeight: 0.6 });

      // Update position for behavior section with added margin
      subjY += presentCellHeight - 8; // Added 4 units margin

      // Child behavior - with dynamic cell background
      doc.setFillColor(...colors.white);
      doc.roundedRect(margin + 4, subjY - 2, pageWidth - (margin * 2) - 8, behaviorCellHeight, 2, 2, 'F');
      doc.setDrawColor(...colors.gray);
      doc.setLineWidth(0.3);
      doc.roundedRect(margin + 4, subjY - 2, pageWidth - (margin * 2) - 8, behaviorCellHeight, 2, 2, 'S');
      
      addText('During the session, child was noted of the following: ', margin + 6, subjY + 2, { fontSize: 10, maxWidth: pageWidth - (margin * 2) - 12, lineHeight: 0.6, fontWeight: 'bold' });
      addText(behaviorText, margin + 8, subjY + 7, { fontSize: 10, maxWidth: pageWidth - (margin * 2) - 14, lineHeight: 0.4 });

      // Update yPosition to account for dynamic subjective section
      yPosition += subjectiveSectionHeight + 5;
      
      // ACTIVITIES SECTION - Make cell heights dynamic
      const activities = Object.entries(formData.activities).slice(0, 6);
      const numberOfRows = Math.ceil(activities.length / 2);
      
      // Calculate dynamic cell heights for each activity
      const cellWidth = (pageWidth - (margin * 2) - 12) / 2;
      let maxRowHeights = [];
      
      // Calculate height needed for each row
      for (let row = 0; row < numberOfRows; row++) {
        let maxHeightInRow = 41; // Minimum height
        
        for (let col = 0; col < 2; col++) {
          const activityIndex = row * 2 + col;
          if (activityIndex < activities.length) {
            const [key, activity] = activities[activityIndex];
            
            // Calculate required height for this activity
            let requiredHeight = 10; // Base height for activity name and supports
            
            // Calculate height for activity name
            if (activity.name) {
              const nameLines = doc.splitTextToSize(activity.name, cellWidth - 20); // Reduced width for activity name
              requiredHeight += nameLines.length * 1.0;
            }
            
            if (activity.performance_notes) {
              const performanceLines = doc.splitTextToSize(activity.performance_notes, cellWidth - 6);
              requiredHeight += performanceLines.length * 8 * 0.4 + 8; // Performance notes height
            }
            
            maxHeightInRow = Math.max(maxHeightInRow, requiredHeight);
          }
        }
        maxRowHeights[row] = maxHeightInRow;
      }
      
      const pageBottomAct = pageHeight - margin;
      const activityChunkOverheadLast = 22;
      const activityChunkOverheadMid = 18;

      let startRow = 0;
      while (startRow < numberOfRows) {
        let endRow = startRow;
        let chunkGrid = 0;

        while (endRow < numberOfRows) {
          const candidateChunk = chunkGrid + maxRowHeights[endRow] + 2;
          const wouldCompleteSection = endRow + 1 >= numberOfRows;
          const overhead = wouldCompleteSection ? activityChunkOverheadLast : activityChunkOverheadMid;
          if (yPosition + candidateChunk + overhead > pageBottomAct) {
            break;
          }
          chunkGrid = candidateChunk;
          endRow++;
        }

        if (endRow === startRow) {
          if (yPosition > margin + 1) {
            doc.addPage();
            yPosition = margin;
            continue;
          }
          chunkGrid = maxRowHeights[startRow] + 2;
          endRow = startRow + 1;
        }

        const isLastChunk = endRow >= numberOfRows;
        const activitySectionHeight =
          chunkGrid + (isLastChunk ? activityChunkOverheadLast : activityChunkOverheadMid);

        const activitySectionTitle =
          startRow === 0 ? 'ACTIVITIES & PERFORMANCE' : 'ACTIVITIES & PERFORMANCE (cont.)';

        const actY = createColoredSection(
          activitySectionTitle,
          margin,
          yPosition,
          pageWidth - (margin * 2),
          activitySectionHeight,
          colors.green,
          colors.lightGreen
        );

        let currentRowY = actY - 2;

        for (let index = startRow * 2; index < Math.min(endRow * 2, activities.length); index++) {
          const row = Math.floor(index / 2);
          const col = index % 2;
          const cellX = margin + 4 + col * (cellWidth + 4);
          const cellHeight = maxRowHeights[row];
          const [, activity] = activities[index];

          if (col === 0 && row > startRow) {
            currentRowY += maxRowHeights[row - 1] + 2;
          }

          const cellY = currentRowY;

          doc.setFillColor(...colors.white);
          doc.roundedRect(cellX, cellY, cellWidth, cellHeight, 2, 2, 'F');
          doc.setDrawColor(...colors.gray);
          doc.setLineWidth(0.3);
          doc.roundedRect(cellX, cellY, cellWidth, cellHeight, 2, 2, 'S');

          let cellContentY = cellY + 3;

          addText(`Activity ${index + 1}:`, cellX + 2, cellContentY + 1, { fontSize: 9, fontWeight: 'bold' });
          if (activity.name) {
            cellContentY = addText(activity.name, cellX + 18, cellContentY + 1, {
              fontSize: 9,
              maxWidth: cellWidth - 20,
              lineHeight: 0.4,
            });
          }

          if (activity.supports.length > 0) {
            addText('Supports Given: ', cellX + 2, cellContentY + 2, {
              fontSize: 8,
              maxWidth: cellWidth - 4,
              lineHeight: 1.0,
              fontWeight: 'bold',
            });
            addText(activity.supports.join(', '), cellX + 25, cellContentY + 2, {
              fontSize: 8,
              maxWidth: cellWidth - 4,
              lineHeight: 1.0,
            });
          }

          if (activity.performance_notes) {
            addText('Performance: ', cellX + 2, cellContentY + 6, {
              fontSize: 8,
              maxWidth: cellWidth - 4,
              lineHeight: 0.2,
              fontWeight: 'bold',
            });
            addText(activity.performance_notes, cellX + 2, cellContentY + 10, {
              fontSize: 8,
              maxWidth: cellWidth - 3,
              lineHeight: 0.4,
            });
          }
        }

        if (isLastChunk) {
          const legendY = actY + chunkGrid - 2;
          doc.setFillColor(...colors.lightGray);
          doc.rect(margin + 4, legendY, pageWidth - (margin * 2) - 8, 6, 'F');
          addText(
            'Legend: IND (independent), CGA (contact guard assistance), MIN (minimal), MOD (moderate), MAX (maximum), VER (verbal), PHY (physical), GES (gestural)',
            margin + 6,
            legendY + 4,
            { fontSize: 6, fontWeight: 'bold', color: colors.gray }
          );
        }

        yPosition += activitySectionHeight + 1;

        startRow = endRow;
        if (startRow < numberOfRows) {
          doc.addPage();
          yPosition = margin;
        }
      }
      
      // SIDE-BY-SIDE: OVERALL PROGRESS AND PLAN/RECOMMENDATIONS SECTIONS
      const sectionWidth = (pageWidth - (margin * 2)) / 2; // Split into two columns with gap
      const leftSectionX = margin;
      const rightSectionX = margin + sectionWidth + 0.5;
      const sectionWrapW = sectionWidth - 12;
      const progressValueWrapW = sectionWidth - 26;
      const planValueWrapW = sectionWidth - 22;
      const recommendationsTrimmed =
        formData.recommendations && String(formData.recommendations).trim()
          ? String(formData.recommendations).trim()
          : '';
      const fullRecWrapW = pageWidth - margin * 2 - 16;
      const bodyFs = 9;
      const lhCol = 0.68;
      const titledPadTop = 6;
      const titledGapTitleBody = 4;
      const titledPadBottom = 16;
      /** Extra space below last baseline (descenders, rounding, smart quotes). */
      const titledBottomSlack = bodyFs * 1.2;

      const splitWrap = (w) => Math.max(8, w - 6);

      /** Wrap each newline-separated paragraph separately (splitTextToSize can mishandle long multi-line blobs). */
      const buildWrappedBodyLines = (body, innerMaxWidth) => {
        doc.setFontSize(bodyFs);
        doc.setFont('helvetica', 'normal');
        const normalized = String(body ?? '').replace(/\r\n/g, '\n');
        const linesOut = [];
        normalized.split('\n').forEach((segment) => {
          const s = segment.replace(/\u00a0/g, ' ').trim();
          if (!s) return;
          doc.splitTextToSize(s, innerMaxWidth).forEach((wl) => linesOut.push(wl));
        });
        return linesOut;
      };

      const computeTitledBoxLayout = (title, body, wrapW) => {
        const w = splitWrap(wrapW);
        doc.setFontSize(bodyFs);
        doc.setFont('helvetica', 'bold');
        const titleLines = doc.splitTextToSize(String(title), w);
        doc.setFont('helvetica', 'normal');
        let bodyLines = buildWrappedBodyLines(body, w);
        if (!bodyLines.length && String(body ?? '').trim()) {
          bodyLines = doc.splitTextToSize(String(body).trim(), w);
        }
        const titleTopOffset = titledPadTop + bodyFs * 1.05;
        const titleDepth = titleLines.length * bodyFs * lhCol;
        const bodyDepth = bodyLines.length * bodyFs * lhCol;
        const h =
          titleTopOffset +
          titleDepth +
          titledGapTitleBody +
          bodyDepth +
          titledPadBottom +
          titledBottomSlack;
        return { h, w, titleLines, bodyLines, titleTopOffset };
      };

      const getTitledBoxHeight = (title, body, wrapW) =>
        computeTitledBoxLayout(title, body, wrapW).h;

      const drawTitledWhiteBox = (x, top, boxWidth, title, body, wrapW) => {
        const { h, titleLines, bodyLines, titleTopOffset } = computeTitledBoxLayout(title, body, wrapW);
        doc.setFillColor(...colors.white);
        doc.roundedRect(x, top, boxWidth, h, 2, 2, 'F');
        doc.setDrawColor(...colors.gray);
        doc.setLineWidth(0.3);
        doc.roundedRect(x, top, boxWidth, h, 2, 2, 'S');

        doc.setTextColor(...colors.darkGray);
        doc.setFontSize(bodyFs);
        let lineY = top + titleTopOffset;
        doc.setFont('helvetica', 'bold');
        titleLines.forEach((line, i) => {
          doc.text(line, x + 2, lineY + i * bodyFs * lhCol);
        });
        lineY += titleLines.length * bodyFs * lhCol + titledGapTitleBody;
        doc.setFont('helvetica', 'normal');
        bodyLines.forEach((line, i) => {
          doc.text(line, x + 2, lineY + i * bodyFs * lhCol);
        });
        return h;
      };

      const progressBlockHeight = () => {
        if (!formData.overall_progress) return 0;
        doc.setFontSize(bodyFs);
        doc.setFont('helvetica', 'normal');
        const lines = doc.splitTextToSize(formData.overall_progress, progressValueWrapW);
        return Math.max(14, lines.length * bodyFs * 0.6 + 16);
      };

      const planBlockHeight = () => {
        if (!formData.plan_action) return 0;
        doc.setFontSize(bodyFs);
        doc.setFont('helvetica', 'normal');
        const lines = doc.splitTextToSize(formData.plan_action, planValueWrapW);
        return Math.max(14, lines.length * bodyFs * 0.6 + 16);
      };

      const progressH = progressBlockHeight();
      const planH = planBlockHeight();

      let leftSectionHeight = 14;
      if (formData.overall_progress) leftSectionHeight += progressH;
      if (formData.key_notes) {
        const keyH = getTitledBoxHeight('KEY NOTES:', formData.key_notes, sectionWrapW);
        const keyLead = formData.overall_progress ? -6 : 4;
        leftSectionHeight += keyLead + keyH;
      }
      leftSectionHeight = Math.max(50, leftSectionHeight);

      let planCursor = 14;
      let rightContentBottom = 14;
      if (formData.plan_action) {
        planCursor += planH + 1;
        rightContentBottom = planCursor;
      }
      if (formData.home_instruction_program) {
        const hipH = getTitledBoxHeight(
          'HIP (Home Instruction Program):',
          formData.home_instruction_program,
          sectionWrapW
        );
        const hipLead = formData.plan_action ? -6 : 4;
        const hipTopRel = planCursor + hipLead;
        rightContentBottom = Math.max(rightContentBottom, hipTopRel + hipH);
        planCursor += hipH - 8;
      }

      const rightSectionHeight = Math.max(50, rightContentBottom + 8);

      const finalSectionHeight = Math.max(leftSectionHeight, rightSectionHeight);

      checkPageBreak(finalSectionHeight + 12);

      let progressY = createColoredSection('OVERALL PROGRESS', leftSectionX, yPosition, sectionWidth, finalSectionHeight, colors.primary, colors.lightBlue);

      if (formData.overall_progress) {
        const progressText = formData.overall_progress;
        const progressCellHeight = progressH;

        doc.setFillColor(...colors.white);
        doc.roundedRect(leftSectionX + 4, progressY - 2, sectionWidth - 8, progressCellHeight - 6, 2, 2, 'F');
        doc.setDrawColor(...colors.gray);
        doc.setLineWidth(0.3);
        doc.roundedRect(leftSectionX + 4, progressY - 2, sectionWidth - 8, progressCellHeight - 6, 2, 2, 'S');

        addText('Progress: ', leftSectionX + 6, progressY + 2, { fontSize: 9, fontWeight: 'bold' });
        addText(progressText, leftSectionX + 22, progressY + 2, { fontSize: bodyFs, maxWidth: progressValueWrapW, lineHeight: 0.6 });
        progressY += progressCellHeight;
      }

      if (formData.key_notes) {
        const keyTop = formData.overall_progress ? progressY - 6 : progressY + 4;
        drawTitledWhiteBox(
          leftSectionX + 4,
          keyTop,
          sectionWidth - 8,
          'KEY NOTES:',
          formData.key_notes,
          sectionWrapW
        );
      }

      let planY = createColoredSection('PLAN / RECOMMENDATIONS', rightSectionX, yPosition, sectionWidth, finalSectionHeight, colors.purple, colors.lightPurple);

      if (formData.plan_action) {
        const planText = formData.plan_action;
        const planCellHeight = planH;

        doc.setFillColor(...colors.white);
        doc.roundedRect(rightSectionX + 4, planY - 2, sectionWidth - 8, planCellHeight - 6, 2, 2, 'F');
        doc.setDrawColor(...colors.gray);
        doc.setLineWidth(0.3);
        doc.roundedRect(rightSectionX + 4, planY - 2, sectionWidth - 8, planCellHeight - 6, 2, 2, 'S');

        addText('Plan: ', rightSectionX + 6, planY + 2, { fontSize: 9, fontWeight: 'bold' });
        addText(planText, rightSectionX + 17, planY + 2, { fontSize: bodyFs, maxWidth: planValueWrapW, lineHeight: 0.6 });
        planY += planCellHeight + 1;
      }

      if (formData.home_instruction_program) {
        const hipTop = formData.plan_action ? planY - 6 : planY + 4;
        const hipDrawnH = drawTitledWhiteBox(
          rightSectionX + 4,
          hipTop,
          sectionWidth - 8,
          'HIP (Home Instruction Program):',
          formData.home_instruction_program,
          sectionWrapW
        );
        planY += hipDrawnH - 8;
      }

      // Update yPosition to account for dynamic sections
      yPosition += finalSectionHeight + 1;

      if (recommendationsTrimmed) {
        checkPageBreak(
          getTitledBoxHeight('Additional Recommendations:', recommendationsTrimmed, fullRecWrapW) + 40
        );
        const recTop = yPosition + 4;
        const recDrawnH = drawTitledWhiteBox(
          margin + 4,
          recTop,
          pageWidth - margin * 2 - 8,
          'Additional Recommendations:',
          recommendationsTrimmed,
          fullRecWrapW
        );
        yPosition += recDrawnH + 10;
      }

      // Check if we need a page break for signature
      checkPageBreak(30);
      
      // THERAPIST SIGNATURE (matching your modal's therapist info section)
      doc.setFillColor(...colors.lightGray);
      doc.roundedRect(margin, yPosition, pageWidth - (margin * 2), 25, 3, 3, 'F');
      
      yPosition += 4;
      addText('Prepared by:', margin + 4, yPosition, { fontSize: 8, fontWeight: 'bold' });
      yPosition += 10;
      if (therapistInfo) {
        addText(`${therapistInfo.name}, ${getSpecializationInfo().designation}`, margin + 4, yPosition, { fontSize: 9, fontWeight: 'bold' });
        yPosition += 4;
        addText('Therapist In-Charge', margin + 4, yPosition, { fontSize: 8, color: colors.gray });
        //addText(getSpecializationInfo().full, margin + 4, yPosition, { fontSize: 8, color: colors.gray });
      }
      
      // Contact info
      yPosition += 3;
      addText('Tiny Triumphs Therapy Center', margin + 4, yPosition, { fontSize: 7, color: colors.gray });
      
      // Signature - use saved signature image if available, otherwise show signature line
      if (therapistInfo?.signature_image) {
        try {
          // Add the signature image
          doc.addImage(therapistInfo.signature_image, 'PNG', margin + 4, yPosition - 16, 40, 8);
        } catch (error) {
          console.warn('Error adding signature image:', error);
          // Fallback to signature line if image fails
          doc.setDrawColor(...colors.gray);
          doc.line(pageWidth - margin - 50, yPosition - 4, pageWidth - margin - 10, yPosition - 4);
          addText('Signature', pageWidth - margin - 45, yPosition - 1, { fontSize: 7, color: colors.gray });
        }
      } else {
        // No signature image available, show signature line
        doc.setDrawColor(...colors.gray);
        doc.line(pageWidth - margin - 50, yPosition - 4, pageWidth - margin - 10, yPosition - 4);
        addText('Signature', pageWidth - margin - 45, yPosition - 1, { fontSize: 7, color: colors.gray });
      }
      
      // INSTEAD OF doc.save(), return the PDF as a blob and filename
      const pdfBlob = doc.output('blob');
      
      const clientName = session.client.name.replace(/\s+/g, '_');
      const sessionDate = new Date(formData.session_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\//g, '-');
      const timeStr = session.time ? session.time.replace(/:/g, '') : 'NoTime';
      const therapistInitial = therapistInfo?.name ? 
        therapistInfo.name.split(' ').map(name => name[0]).join('') : 'T';
      const abbrev = getSpecializationInfo().abbrev;
      
      // Include time and therapist initial to make filename unique
      const filename = `${abbrev}_Session_Note_${clientName}_${sessionDate}_${timeStr}_${therapistInitial}.pdf`;
      
      return { pdfBlob, filename };
      
    } catch (error) {
      console.error('Error generating PDF for upload:', error);
      throw error;
    }
  };  

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
  
    try {
      const noteData = {
        session_id: session.attendance?.id || null, // Can be null if no attendance yet
        original_session_id: session.id, // Always store the original session ID
        session_time: session.time, // Store the time
        session_day: new Date(formData.session_date).toLocaleDateString('en-US', { weekday: 'long' }), // Store the day
        client_id: session.client_id,
        therapist_id: therapistId,
        session_date: formData.session_date,
        specialization: therapistInfo?.specialization || 'Occupational Therapy',
        present_with: formData.present_with,
        present_with_other: formData.present_with_other,
        child_behavior: formData.child_behavior,
        child_behavior_other: formData.child_behavior_other,
        activities: formData.activities,
        overall_progress: formData.overall_progress,
        key_notes: formData.key_notes,
        plan_action: formData.plan_action,
        home_instruction_program: formData.home_instruction_program,
        recommendations: formData.recommendations
      };
  
      let sessionNoteId;
  
      if (existingNote) {
        // Update existing note
        const { error } = await supabase
          .from('session_notes')
          .update(noteData)
          .eq('id', existingNote.id);
        
        if (error) throw error;
        sessionNoteId = existingNote.id;
      } else {
        // Create new note
        const { data: newNote, error } = await supabase
          .from('session_notes')
          .insert([noteData])
          .select()
          .single();
        
        if (error) throw error;
        sessionNoteId = newNote.id;
      }
  
      // Generate and upload PDF to client_documents
      try {
        const { pdfBlob, filename } = await generateAndUploadPDF();
        
        // Create file path for storage
        const fileExt = 'pdf';
        const storageFileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `${session.client_id}/session_notes/${storageFileName}`;
  
        // Upload PDF to Supabase Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('client-documents')
          .upload(filePath, pdfBlob, {
            contentType: 'application/pdf'
          });
  
        if (uploadError) throw uploadError;
  
        // Create unique title and description that includes session time and therapist
        const sessionDate = new Date(formData.session_date);
        const dateStr = sessionDate.toLocaleDateString();
        const timeStr = session.time || 'Unknown Time';
        const therapistName = therapistInfo?.name || 'Therapist';
        
        // Make title unique by including time and therapist initial
        const therapistInitial = therapistName.split(' ').map(name => name[0]).join('');
        const uniqueTitle = `${getSpecializationInfo().abbrev} Session Note - ${dateStr} ${timeStr} (${therapistInitial})`;
        
        // Check if there's an existing document record for this SPECIFIC session note
        // Use session note ID or unique combination to avoid conflicts
        const { data: existingDoc, error: docCheckError } = await supabase
          .from('client_documents')
          .select('id')
          .eq('client_id', session.client_id)
          .eq('document_category', 'Session Notes')
          .eq('title', uniqueTitle)
          .maybeSingle();
  
        if (docCheckError && docCheckError.code !== 'PGNF') throw docCheckError;
  
        const documentData = {
          client_id: session.client_id,
          document_category: 'Session Notes',
          document_type: 'Session Note',
          title: uniqueTitle,
          description: `Session note by ${therapistName} on ${dateStr} at ${timeStr}`,
          file_name: filename,
          file_path: filePath,
          file_size: pdfBlob.size,
          mime_type: 'application/pdf',
        };
  
        if (existingDoc) {
          // Update existing document record
          const { error: updateDocError } = await supabase
            .from('client_documents')
            .update(documentData)
            .eq('id', existingDoc.id);
          
          if (updateDocError) throw updateDocError;
        } else {
          // Insert new document record
          const { error: insertDocError } = await supabase
            .from('client_documents')
            .insert([documentData]);
          
          if (insertDocError) throw insertDocError;
        }
  
        console.log('PDF uploaded and document record saved successfully');
  
      } catch (pdfError) {
        console.error('Error handling PDF upload:', pdfError);
        // Don't throw here - session note was saved successfully, PDF upload is secondary
        toast({
          title: 'Session note saved, but PDF upload failed',
          description: 'The session note was saved but the PDF could not be uploaded to documents.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
  
      toast({
        title: existingNote ? 'Session note updated' : 'Session note saved',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
  
      // Reset form after successful save for new notes only
      if (!existingNote) {
        setFormData(getInitialFormData());
        setActivityCount(1);
        setLastSessionAttendanceId(null); // Reset session tracking
      }
  
      onSave?.();
      onClose();
      
    } catch (error) {
      toast({
        title: 'Error saving session note',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateActivity = (activityKey, field, value) => {
    // Normalize text input to handle potential formatting issues
    const normalizedValue = field === 'performance_notes' && typeof value === 'string' 
      ? value.replace(/\r\n/g, '\n').replace(/\r/g, '\n') 
      : value;
      
    setFormData(prev => ({
      ...prev,
      activities: {
        ...prev.activities,
        [activityKey]: {
          ...prev.activities[activityKey],
          [field]: normalizedValue
        }
      }
    }));
  };

  const addActivity = () => {
    const newCount = activityCount + 1;
    const newActivityKey = `activity_${newCount}`;
    
    setFormData(prev => ({
      ...prev,
      activities: {
        ...prev.activities,
        [newActivityKey]: { name: '', supports: [], performance_notes: '' }
      }
    }));
    
    setActivityCount(newCount);
  };

  const removeActivity = (activityKey) => {
    if (activityCount <= 1) return; // Don't allow removing the last activity
    
    setFormData(prev => {
      const newActivities = { ...prev.activities };
      delete newActivities[activityKey];
      return {
        ...prev,
        activities: newActivities
      };
    });
    
    setActivityCount(prev => prev - 1);
  };

  // Get specialization abbreviation and full name
  const getSpecializationInfo = () => {
    const specialization = therapistInfo?.specialization || 'Occupational Therapy';
    
    switch (specialization) {
      case 'Occupational Therapy':
        return { abbrev: 'OT', full: 'Occupational Therapist', designation: 'OTRP' };
      case 'Speech Therapy':
        return { abbrev: 'ST', full: 'Speech Therapist', designation: 'RSLP' };
      case 'Physical Therapy':
        return { abbrev: 'PT', full: 'Physical Therapist', designation: 'PTRP' };
      case 'Play Group':
        return { abbrev: 'PG', full: 'Play Group Facilitator', designation: 'PGF' };
      default:
        return { abbrev: 'OT', full: 'Occupational Therapist', designation: 'OTRP' };
    }
  };

  // Configuration options
  const presentWithOptions = ['Parent', 'Caregiver', 'Other'];
  
  const behaviorOptions = [
    'High arousal', 'Low arousal', 'Resistant', 'Needed co-regulation', 
    'Distracted/Impulsive', 'Engaged', 'Regulated', 'Cooperative'
  ];
  
  const supportOptions = [
    { value: 'IND', label: 'IND (Independent)' },
    { value: 'CGA', label: 'CGA (Contact Guard Assistance)' },
    { value: 'MIN', label: 'MIN (Minimal)' },
    { value: 'MOD', label: 'MOD (Moderate)' },
    { value: 'MAX', label: 'MAX (Maximum)' },
    { value: 'VER', label: 'VER (Verbal)' },
    { value: 'PHY', label: 'PHY (Physical)' },
    { value: 'GES', label: 'GES (Gestural)' }
  ];

  if (!session) return null;

  const specializationInfo = getSpecializationInfo();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxH="90vh" overflowY="auto">
        <form onSubmit={handleSubmit}>
        <ModalHeader bg="#24b5e8" color="white">
          <VStack align="start" spacing={{ base: 1, md: 0 }}>
            <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold">
              {specializationInfo.abbrev} SESSION NOTES
            </Text>
            
            {/* Mobile: Stack vertically, Desktop: Horizontal */}
            <Stack 
              direction={{ base: "column", md: "row" }} 
              spacing={{ base: 0, md: 2 }}
              align={{ base: "start", md: "center" }}
            >
              <HStack spacing={1} flexWrap="wrap">
                <Text fontSize={{ base: "sm", md: "md" }} fontWeight="normal">
                  {session.client.name}
                </Text>
                {session.client.nickname && (
                  <Text fontSize={{ base: "sm", md: "md" }} fontWeight="normal">
                    "{session.client.nickname}"
                  </Text>
                )}
              </HStack>
              
              <Text 
                fontSize={{ base: "sm", md: "md" }} 
                fontWeight="normal"
                display={{ base: "block", md: "inline" }}
              >
                {new Date(formData.session_date).toLocaleDateString()}
              </Text>
            </Stack>
          </VStack>
        </ModalHeader>
          <ModalCloseButton color="white" />
          
          <ModalBody p={6}>
            <VStack spacing={6} align="stretch">
              {existingNote && (
                <Alert status="info">
                  <AlertIcon />
                  You are editing an existing session note.
                </Alert>
              )}

              {/* SUBJECTIVE Section */}
              <Box bg="yellow.50" p={4} borderRadius="md">
                <Text fontWeight="bold" mb={3} color="black" bg="#ffac11" p={2}>
                  SUBJECTIVE
                </Text>
                
                <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>Present with:</FormLabel>
                  <CheckboxGroup 
                    value={formData.present_with} 
                    onChange={(value) => setFormData(prev => ({ ...prev, present_with: value }))}
                  >
                    <Stack direction={{ base: "column", md: "row" }} spacing={{ base: 2, md: 4 }}>
                      {presentWithOptions.map(option => (
                        <Checkbox key={option} value={option} size={{ base: "sm", md: "md" }}>
                          {option}
                        </Checkbox>
                      ))}
                    </Stack>
                  </CheckboxGroup>
                  {formData.present_with.includes('Other') && (
                    <Input
                      mt={2}
                      placeholder="Specify other"
                      value={formData.present_with_other}
                      onChange={(e) => setFormData(prev => ({ ...prev, present_with_other: e.target.value }))}
                      fontSize={{ base: "sm", md: "md" }}
                    />
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>
                    During the session, child was noted to be:
                  </FormLabel>
                  <CheckboxGroup 
                    value={formData.child_behavior} 
                    onChange={(value) => setFormData(prev => ({ ...prev, child_behavior: value }))}
                  >
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={2}>
                      {behaviorOptions.map(option => (
                        <Checkbox key={option} value={option} size="sm">
                          <Text fontSize={{ base: "xs", md: "sm" }}>{option}</Text>
                        </Checkbox>
                      ))}
                    </SimpleGrid>
                  </CheckboxGroup>
                  <Input
                    mt={2}
                    placeholder="Others (specify)"
                    value={formData.child_behavior_other}
                    onChange={(e) => setFormData(prev => ({ ...prev, child_behavior_other: e.target.value }))}
                    fontSize={{ base: "sm", md: "md" }}
                  />
                </FormControl>
                </VStack>
              </Box>

              {/* ACTIVITIES & PERFORMANCE Section */}
              <Box bg="green.50" p={4} borderRadius="md">
              <VStack spacing={3} align="stretch">
                {/* Mobile: Stack vertically, Desktop: Side by side */}
                <Stack 
                  direction={{ base: "column", md: "row" }} 
                  justify="space-between" 
                  align={{ base: "stretch", md: "center" }}
                  spacing={3}
                >
                  <HStack flex={1} display={{ base: "none", md: "flex" }}>
                    <Text fontWeight="bold" color="black" bg="#abdb15" p={2} flex={1}>
                      ACTIVITIES
                    </Text>
                    <Text fontWeight="bold" color="black" bg="#abdb15" p={2} flex={1}>
                      PERFORMANCE
                    </Text>
                  </HStack>
                  
                  {/* Mobile header */}
                  <Text 
                    fontWeight="bold" 
                    color="black" 
                    bg="#abdb15" 
                    p={2} 
                    display={{ base: "block", md: "none" }}
                    textAlign="center"
                  >
                    ACTIVITIES & PERFORMANCE
                  </Text>
                </Stack>
              </VStack>

                <VStack spacing={4} align="stretch">
                  {Object.keys(formData.activities).map((activityKey, index) => {
                    const activityNum = index + 1;
                    const activity = formData.activities[activityKey];
                    
                    return (
                      <Grid 
                        key={activityKey} 
                        templateColumns={{ base: "1fr", md: "1fr 1fr" }} 
                        gap={4} 
                        p={3} 
                        borderWidth="1px" 
                        borderRadius="md" 
                        bg="white" 
                        position="relative"
                      >
                        {/* Remove button */}
                        {activityCount > 1 && (
                          <IconButton
                            icon={<DeleteIcon />}
                            size="xs"
                            colorScheme="red"
                            variant="outline"
                            position="absolute"
                            top={2}
                            right={2}
                            onClick={() => removeActivity(activityKey)}
                            aria-label="Remove activity"
                          />
                        )}
                        
                        <GridItem>
                          <VStack align="stretch" spacing={2}>
                            <Text fontWeight="medium">Activity {activityNum}:</Text>
                            <Input
                              placeholder={`Activity ${activityNum} description`}
                              value={activity.name}
                              onChange={(e) => updateActivity(activityKey, 'name', e.target.value)}
                              fontSize={{ base: "sm", md: "md" }}
                            />
                            <Text fontSize="sm" fontWeight="medium">Supports given:</Text>
                            <CheckboxGroup 
                              value={activity.supports} 
                              onChange={(value) => updateActivity(activityKey, 'supports', value)}
                            >
                              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={1}>
                                {supportOptions.map(support => (
                                  <Checkbox key={support.value} value={support.value} size="sm">
                                    {support.value}
                                  </Checkbox>
                                ))}
                              </SimpleGrid>
                            </CheckboxGroup>
                          </VStack>
                        </GridItem>
                        
                        <GridItem>
                          <VStack align="stretch" spacing={2}>
                            <Text fontWeight="medium">Performance Notes:</Text>
                            <Textarea
                              placeholder="Describe child's performance..."
                              value={activity.performance_notes || ''}
                              onChange={(e) => updateActivity(activityKey, 'performance_notes', e.target.value)}
                              rows={{ base: 3, md: 4 }}
                              resize="vertical"
                              whiteSpace="pre-wrap"
                              fontSize={{ base: "sm", md: "md" }}
                            />
                          </VStack>
                        </GridItem>
                      </Grid>
                    );
                  })}
                </VStack>

                {/* Add Activity Button - positioned below activities for better mobile UX */}
                <Button
                  leftIcon={<AddIcon />}
                  size={{ base: "sm", md: "sm" }}
                  colorScheme="green"
                  onClick={addActivity}
                  width="full"
                  mt={4}
                >
                  Add Activity
                </Button>

                {/* Legend */}
                <Box mt={4} p={3} bg="gray.100" borderRadius="md">
                  <Text fontSize="xs" color="gray.700">
                    <strong>Legend:</strong> IND (independent) CGA (contact guard assistance) MIN (minimal) MOD (moderate) MAX (maximum) VER (verbal) PHY (physical) GES (gestural)
                  </Text>
                </Box>
              </Box>

              {/* OVERALL PROGRESS Section */}
              <Box bg="blue.50" p={4} borderRadius="md">
                <Text fontWeight="bold" mb={3} color="black" bg="#24b5e8" p={2}>
                  OVERALL PROGRESS
                </Text>
                
                <VStack spacing={4} align="stretch">
                <FormControl>
                  <Stack direction={{ base: "column", md: "row" }} spacing={{ base: 2, md: 4 }}>
                    {['Progressing', 'Maintaining', 'Regression'].map(option => (
                      <Checkbox
                        key={option}
                        isChecked={formData.overall_progress === option}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          overall_progress: e.target.checked ? option : '' 
                        }))}
                        size={{ base: "sm", md: "md" }}
                      >
                        {option}
                      </Checkbox>
                    ))}
                  </Stack>
                </FormControl>

                  <FormControl>
                    <FormLabel>KEY NOTES:</FormLabel>
                    <Textarea
                      value={formData.key_notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, key_notes: e.target.value }))}
                      placeholder="Key observations and notes about the session..."
                      rows={3}
                    />
                  </FormControl>
                </VStack>
              </Box>

              {/* PLAN/RECOMMENDATIONS Section */}
              <Box bg="purple.50" p={4} borderRadius="md">
                <Text fontWeight="bold" mb={3} color="black" bg="#dc4291" p={2}>
                  PLAN / RECOMMENDATIONS
                </Text>
                
                <VStack spacing={4} align="stretch">
                <FormControl>
                  <Stack direction={{ base: "column", md: "row" }} spacing={{ base: 2, md: 4 }}>
                    {['Continue', 'Modify'].map(option => (
                      <Checkbox
                        key={option}
                        isChecked={formData.plan_action === option}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          plan_action: e.target.checked ? option : '' 
                        }))}
                        size={{ base: "sm", md: "md" }}
                      >
                        {option}
                      </Checkbox>
                    ))}
                  </Stack>
                </FormControl>

                  <FormControl>
                    <FormLabel>HIP (Home Instruction Program):</FormLabel>
                    <Textarea
                      value={formData.home_instruction_program}
                      onChange={(e) => setFormData(prev => ({ ...prev, home_instruction_program: e.target.value }))}
                      placeholder="Home instruction program recommendations..."
                      rows={3}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Additional Recommendations:</FormLabel>
                    <Textarea
                      value={formData.recommendations}
                      onChange={(e) => setFormData(prev => ({ ...prev, recommendations: e.target.value }))}
                      placeholder="Additional recommendations and next steps..."
                      rows={3}
                    />
                  </FormControl>
                </VStack>
              </Box>

              {/* Therapist Info */}
              {therapistInfo && (
                <Box bg="gray.50" p={4} borderRadius="md">
                  <Text fontWeight="bold" mb={2}>Prepared by:</Text>
                  <Text fontWeight="bold">{`${therapistInfo.name}, ${getSpecializationInfo().designation}`}</Text>
                  <Text fontSize="sm" color="gray.600">Therapist In-Charge</Text>
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    Tiny Triumphs Therapy Center
                  </Text>
                </Box>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter bg="gray.50">
            <HStack spacing={3}>
              <Button 
                type="submit" 
                colorScheme="blue" 
                isLoading={isSubmitting}
                loadingText={existingNote ? "Updating..." : "Saving..."}
              >
                {existingNote ? 'Update Session Note' : 'Save Session Note'}
              </Button>
              {(existingNote || formData.activities.activity_1?.name) && (
                <Button 
                  colorScheme="purple" 
                  variant="outline"
                  onClick={generatePDF}
                  isLoading={isExporting}
                  loadingText="Generating PDF..."
                >
                  Export PDF
                </Button>
              )}
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default SessionNotesModal;